import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  type EmergencyAgentMetadata,
  OutboundCallType,
} from '@super/shared-types'
import { Plus, Trash2 } from 'lucide-react'

const DEFAULT_GREETING_TEMPLATE =
  'There is an emergency at {{propertyName}} {{unit}}. {{customerName}} has reported {{sessionTitle}}. Please check your email for details. Say yes to confirm receipt, or no to decline.'

const DEFAULT_PROMPT = `You are an emergency alert agent. Your job is to notify the team when an emergency has been reported and confirm if they will accept it or not.

If they are not able to accept it, you will notify the next person in the tree.

The person you are calling has also been sent a text message and an email notification with the details, and a work order has been created in Yardi for them.

You may answer basic questions based on the following information if they ask for more details:
- {{propertyName}}
- {{unit}}
- {{customerName}}
- {{sessionTitle}}
- {{sessionDescription}}`

const simulateEmergencySchema = z.object({
  propertyName: z.string().min(1, 'Property name is required'),
  unit: z.string().min(1, 'Unit is required'),
  customerName: z.string().min(1, 'Customer name is required'),
  sessionTitle: z.string().min(1, 'Session title is required'),
  sessionDescription: z.string().optional(),
  greeting: z.string().min(1, 'Greeting is required'),
  prompt: z.string().min(1, 'Prompt (instructions) is required'),
  phoneTree: z
    .array(z.string())
    .refine(
      arr => arr.some(s => s.trim().length > 0),
      'Add at least one phone number to the phone tree.',
    ),
})

type SimulateEmergencyFormValues = z.infer<typeof simulateEmergencySchema>

interface SimulateEmergencyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (data: SimulateEmergencyFormValues) => Promise<void>
}

function interpolateGreeting(
  template: string,
  vars: {
    propertyName: string
    unit: string
    customerName: string
    sessionTitle: string
  },
): string {
  let msg = template
  msg = msg.replace(/\{\{propertyName\}\}/g, vars.propertyName)
  msg = msg.replace(/\{\{unit\}\}/g, vars.unit)
  const resident = vars.customerName?.trim() || 'A resident'
  msg = msg.replace(/\{\{customerName\}\}/g, resident)
  msg = msg.replace(/\{\{sessionTitle\}\}/g, vars.sessionTitle)
  return msg
}

export function SimulateEmergencyModal({
  open,
  onOpenChange,
  onSubmit: onSubmitProp,
}: SimulateEmergencyModalProps) {
  const { currentCompany } = useAppAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<SimulateEmergencyFormValues>({
    resolver: zodResolver(simulateEmergencySchema),
    defaultValues: {
      propertyName: '',
      unit: '',
      customerName: '',
      sessionTitle: '',
      sessionDescription: '',
      greeting: DEFAULT_GREETING_TEMPLATE,
      prompt: DEFAULT_PROMPT,
      phoneTree: [''],
    },
  })

  const phoneTree = form.watch('phoneTree') ?? ['']

  useEffect(() => {
    if (open) {
      form.reset({
        propertyName: '',
        unit: '',
        customerName: '',
        sessionTitle: '',
        sessionDescription: '',
        greeting: DEFAULT_GREETING_TEMPLATE,
        prompt: DEFAULT_PROMPT,
        phoneTree: [''],
      })
    }
  }, [open, form])

  const addPhoneNumber = () =>
    form.setValue('phoneTree', [...form.getValues('phoneTree'), ''])
  const removePhoneNumber = (index: number) =>
    form.setValue(
      'phoneTree',
      form.getValues('phoneTree').filter((_, i) => i !== index),
    )
  const updatePhoneNumber = (index: number, value: string) => {
    const next = [...form.getValues('phoneTree')]
    next[index] = value
    form.setValue('phoneTree', next)
  }

  const onSubmit: SubmitHandler<SimulateEmergencyFormValues> = async data => {
    if (!currentCompany?.id) {
      toast.error('Company ID is missing')
      return
    }
    if (!currentCompany?.phone?.trim()) {
      toast.error(
        'Company phone is required for outbound calls. Please configure a phone number in company settings.',
      )
      return
    }

    const numbers = data.phoneTree.map(p => p.trim()).filter(Boolean)

    setIsSubmitting(true)
    try {
      if (onSubmitProp) {
        await onSubmitProp({ ...data, phoneTree: numbers })
      } else {
        const notificationMessage = interpolateGreeting(data.greeting, {
          propertyName: data.propertyName,
          unit: data.unit,
          customerName: data.customerName,
          sessionTitle: data.sessionTitle,
        })

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6060'
        const roomName = `emergency-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`
        const firstPhone = numbers[0]

        const prepareRes = await fetch(
          `${apiUrl}/api/voice/conferences/outbound/prepare`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              to: firstPhone,
              companyId: currentCompany.id,
              callId: roomName,
            }),
          },
        )
        if (!prepareRes.ok) {
          throw new Error(
            `Failed to prepare outbound session: ${prepareRes.statusText}`,
          )
        }
        const {
          data: { sessionId },
        } = await prepareRes.json()

        const metadata: EmergencyAgentMetadata = {
          callType: OutboundCallType.EMERGENCY,
          companyId: currentCompany.id,
          companyName: currentCompany.name,
          from: currentCompany.phone,
          propertyName: data.propertyName,
          unit: data.unit,
          customerName: data.customerName,
          sessionTitle: data.sessionTitle,
          sessionDescription: data.sessionDescription,
          notificationMessage,
          instructions: data.prompt,
          phoneTree: numbers,
          greeting: notificationMessage,
          sessionId,
          variableValues: {
            propertyName: data.propertyName,
            property: data.propertyName,
            unit: data.unit,
            customerName: data.customerName,
            contactName: data.customerName,
            sessionTitle: data.sessionTitle,
            sessionDescription: data.sessionDescription ?? '',
            issueDescription: data.sessionDescription ?? '',
            companyName: currentCompany.name,
            agentName: currentCompany.name,
          },
        }

        const response = await fetch(`${apiUrl}/api/livekit/dispatch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            agentName: 'outbound-caller',
            roomName,
            metadata,
            envPrefix: 'EMERGENCY_OUTBOUND',
          }),
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(
            `Failed to dispatch agent: ${response.statusText}. ${errorText}`,
          )
        }

        toast.success('Emergency agent dispatched successfully')
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to dispatch emergency agent:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to dispatch emergency agent',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Emergency outbound prompt</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="text-sm font-medium text-muted-foreground">
              Variables
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="propertyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property name</FormLabel>
                    <FormControl>
                      <Input placeholder="Mainstreet" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <Input placeholder="101" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="customerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sessionTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Session title</FormLabel>
                  <FormControl>
                    <Input placeholder="Water leak in bathroom" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sessionDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Session description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional details for the agent to share if asked"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="greeting"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Greeting</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={DEFAULT_GREETING_TEMPLATE}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="prompt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prompt (instructions)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={DEFAULT_PROMPT}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <div className="flex items-center justify-between mb-2">
                <FormLabel>Phone tree</FormLabel>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={addPhoneNumber}
                >
                  <Plus className="h-4 w-4" /> Add number
                </Button>
              </div>
              <div className="space-y-2">
                {phoneTree.map((phone, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={phone}
                      onChange={e => updatePhoneNumber(index, e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => removePhoneNumber(index)}
                      disabled={phoneTree.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              {form.formState.errors.phoneTree?.message && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.phoneTree.message}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Dispatching...' : 'Simulate'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
